import styled, { keyframes } from 'styled-components';
import React, { useEffect, useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import QuestionContent from "./QuestionContent";
import Filters from '../Filters';
import Empty from "../components/EmptyFeedback";
import { filter_data } from "reports/Tag/calculations";
import Anchor from "../Anchor";
import QuestionDistributionChart from "./QuestionDistributionChart";
import Text from "new_components/atoms/Text";
import typography from "styles/typography";
import colors from "styles/colors";

const chartOptions = [
    { name: "Low", color: "#F15062" },
    { name: "Neutral", color: "#B3B3B3" },
    { name: "High", color: "#1D9A7D" },
];





const QuestionAnalytics = ({
    core_data,
    outcomeQ,
    data,
    outcomeStandards,
    navigation,
    rule,
}) => {
    const [selected, setSelected] = useState(null);
    const [correctedData, setCorrectedData] = useState(null);
    const [isFading, setIsFading] = useState(false);
    const [isEmpty, setIsEmpty] = useState(false);
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' for highest to lowest, 'asc' for lowest to highest
    const [isSortTransitioning, setIsSortTransitioning] = useState(false);
    const [isAnchorTransitioning, setIsAnchorTransitioning] = useState(false);
    const [viewMode, setViewMode] = useState('questions'); // 'questions' or 'factors'
    const [neutralBounds, setNeutralBounds] = useState({ min: 5, max: 6.99 });
    const [editingNeutral, setEditingNeutral] = useState(false);
    const [tempNeutralValues, setTempNeutralValues] = useState({ min: '', max: '' });
    
    // Calculate all ranges based on neutral bounds
    const customRanges = {
        Low: { min: 0, max: Math.max(0, neutralBounds.min - 0.01) },
        Neutral: { min: neutralBounds.min, max: neutralBounds.max },
        High: { min: Math.min(10, neutralBounds.max + 0.01), max: 10 }
    };
    const [rangeUpdateTrigger, setRangeUpdateTrigger] = useState(0);

    const selectedFilters = useSelector(
        (state) => state.audit?.new_filters,
        shallowEqual
    );

    

    useEffect(() => {
        if (data.length === 0 && selectedFilters.length === 0) {
            return;
        }

        let filteredData = [];
        if (data?.length === 0) {
            setIsEmpty(true);
            return;
        }

        if (data[0][0]) {
            setIsEmpty(false);
            filteredData = filter_data(data[0], selectedFilters);
            return setCorrectedData(filteredData);
        }
        setIsEmpty(false);
        filteredData = filter_data(data, selectedFilters);
        setCorrectedData(filteredData);
    }, [data, selectedFilters]);

    // Force re-render when neutralBounds change
    useEffect(() => {
        // This will trigger a re-render of the component when ranges change
        // which will cause all calculations to re-run with new ranges
        console.log('Ranges updated, forcing recalculation:', customRanges);
    }, [neutralBounds]);

    // Watch for anchor changes when on question content
    useEffect(() => {
        if (selected && selectedFilters) {
            setIsAnchorTransitioning(true);
            const timer = setTimeout(() => {
                setIsAnchorTransitioning(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [selectedFilters, selected]);

    const handleSelect = (question) => {
        setIsFading(true);
        setTimeout(() => {
            setSelected(question);
            setIsFading(false);
        }, 300);
    };

    const handleBack = () => {
        setIsFading(true);
        setTimeout(() => {
            setSelected(null);
            setIsFading(false);
        }, 300);
    };

    const toggleSortDirection = () => {
        setIsSortTransitioning(true);
        setTimeout(() => {
            setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
            setTimeout(() => {
                setIsSortTransitioning(false);
            }, 50);
        }, 150);
    };

    const handleNeutralEdit = () => {
        setEditingNeutral(true);
        setTempNeutralValues({
            min: neutralBounds.min.toString(),
            max: neutralBounds.max.toString()
        });
    };

    const handleNeutralMinChange = (e) => {
        const value = e?.target?.value ?? '';
        setTempNeutralValues(prev => ({ ...prev, min: value }));
    };

    const handleNeutralMaxChange = (e) => {
        const value = e?.target?.value ?? '';
        setTempNeutralValues(prev => ({ ...prev, max: value }));
    };

    const handleNeutralKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleNeutralSave();
        } else if (e.key === 'Escape') {
            handleNeutralCancel();
        }
    };

    const handleNeutralSave = () => {
        const min = parseFloat(tempNeutralValues.min);
        const max = parseFloat(tempNeutralValues.max);
        
        if (isNaN(min) || isNaN(max) || min >= max || min < 0 || max > 10) {
            alert('Please enter valid range values (min must be less than max, and both between 0-10)');
            return;
        }
        
        setNeutralBounds({ min, max });
        
        // Force recalculation by updating trigger
        setRangeUpdateTrigger(prev => prev + 1);
        
        setEditingNeutral(false);
        setTempNeutralValues({ min: '', max: '' });
    };

    const handleNeutralCancel = () => {
        setEditingNeutral(false);
        setTempNeutralValues({ min: '', max: '' });
    };

    const computeDistributionData = (question) => {
        if (!correctedData) return null;
        
        // Debug: Log the current ranges being used
        console.log('Computing distribution with ranges:', customRanges);
        
        let responses;
        
        if (question.isOutcome) {
            // Extract responses for outcome questions
            responses = correctedData.flatMap(response => {
                if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                    return [];
                }
                return response.employee_outcomes.responses.filter(r => 
                    r.q === question.q && r.s === question.s
                ).map(r => r.response);
            }).filter(val => {
                // Filter out null, undefined, 'N/A' responses and ensure it's a valid number
                return val !== null && 
                       val !== undefined && 
                       val !== 'N/A' && 
                       val !== '' &&
                       !isNaN(Number(val));
            }).map(val => Number(val)); // Convert to numbers to handle both int and float responses
        } else {
            // Extract responses for regular questions by matching dimension, factor, and question index
            responses = correctedData.flatMap(response =>
                response.questions?.filter(q => 
                    q.id === question.dimension && 
                    q.factor === question.factor && 
                    q.q === question.q
                ).map(q => q.response) || []
            ).filter(val => {
                // Filter out null, undefined, 'N/A' responses and ensure it's a valid number
                return val !== null && 
                       val !== undefined && 
                       val !== 'N/A' && 
                       val !== '' &&
                       !isNaN(Number(val));
            }).map(val => Number(val)); // Convert to numbers to handle both int and float responses
        }
        
        if (responses.length === 0) return null;
        
        // Check minimum response threshold based on Whitelabel rule
        const minimumResponses = core_data?.Whitelabel?.rule || 2;
        if (responses.length <= minimumResponses) return null;
        
        // Count responses in each category (updated to handle fractional values)
        const lowCount = responses.filter(score => score >= customRanges.Low.min && score <= customRanges.Low.max).length;
        const neutralCount = responses.filter(score => score >= customRanges.Neutral.min && score <= customRanges.Neutral.max).length;
        const highCount = responses.filter(score => score >= customRanges.High.min && score <= customRanges.High.max).length;
        
        // Debug: Log the counts and ranges being used
        console.log(`Question: ${question.title || 'Unknown'}, Low: ${lowCount} (${customRanges.Low.min}-${customRanges.Low.max}), Neutral: ${neutralCount} (${customRanges.Neutral.min}-${customRanges.Neutral.max}), High: ${highCount} (${customRanges.High.min}-${customRanges.High.max})`);
        
        // Count responses outside the 0-10 range to handle edge cases
        const outsideRangeCount = responses.filter(score => score < 0 || score > 10).length;
        
        // Total valid responses that fall within our expected ranges
        const totalCategorizedResponses = lowCount + neutralCount + highCount;
        
        // If there are responses outside the 0-10 range, log them for debugging
        if (outsideRangeCount > 0) {
            const outsideRangeValues = responses.filter(score => score < 0 || score > 10);
            const questionId = question.isOutcome 
                ? `Outcome-${question.q}-${question.s}` 
                : `${question.dimension}-${question.factor}-${question.q}`;
            console.warn(`Question ${questionId} has responses outside 0-10 range:`, outsideRangeValues);
        }
        
        // Use total categorized responses for percentage calculation to ensure 100% total
        const totalForPercentage = totalCategorizedResponses;
        
        if (totalForPercentage === 0) return null;
        
        const distributionData = [
            { 
                name: "Low", 
                data: [(lowCount / totalForPercentage) * 100], 
                color: chartOptions[0].color, 
                count: lowCount 
            },
            { 
                name: "Neutral", 
                data: [(neutralCount / totalForPercentage) * 100], 
                color: chartOptions[1].color, 
                count: neutralCount 
            },
            { 
                name: "High", 
                data: [(highCount / totalForPercentage) * 100], 
                color: chartOptions[2].color, 
                count: highCount 
            }
        ];
        
        // Verify percentages add up to 100 (with small tolerance for floating point precision)
        const totalPercentage = distributionData.reduce((sum, item) => sum + item.data[0], 0);
        if (Math.abs(totalPercentage - 100) > 0.01) {
            const questionId = question.isOutcome 
                ? `Outcome-${question.q}-${question.s}` 
                : `${question.dimension}-${question.factor}-${question.q}`;
            console.warn(`Percentage total not 100% for question ${questionId}: ${totalPercentage}%`);
        }
        
        return distributionData;
    };

    const computeFactorDistributionData = (factorInfo) => {
        if (!correctedData) return null;
        
        // Calculate per-person averages for the factor
        const personAverages = [];
        
        if (factorInfo.isOutcome) {
            // For outcome categories, calculate average across all questions in that category
            const outcomeCategory = core_data.outcomeQuestions.questions[factorInfo.outcomeCategory];
            if (!outcomeCategory) return null;
            
            correctedData.forEach(response => {
                if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                    return;
                }
                
                // Get all responses for this outcome category from this person
                const personResponses = response.employee_outcomes.responses.filter(r => 
                    outcomeCategory.questions.some(q => q.q === r.q && q.s === r.s)
                ).map(r => r.response).filter(val => {
                    return val !== null && 
                           val !== undefined && 
                           val !== 'N/A' && 
                           val !== '' &&
                           !isNaN(Number(val));
                }).map(val => Number(val));
                
                // Only include this person if they answered at least 50% of questions in this category
                const totalQuestions = outcomeCategory.questions.length;
                if (personResponses.length >= Math.ceil(totalQuestions * 0.5)) {
                    const average = personResponses.reduce((sum, val) => sum + val, 0) / personResponses.length;
                    personAverages.push(average);
                }
            });
        } else {
            // For regular factors, calculate average across all questions in that factor
            const dimension = core_data.questions.dimensions[factorInfo.dimension];
            const factor = dimension?.factors[factorInfo.factor];
            if (!factor) return null;
            
            correctedData.forEach(response => {
                // Get all responses for this factor from this person
                const personResponses = (response.questions || [])
                    .filter(q => 
                        q.id === factorInfo.dimension && 
                        q.factor === factorInfo.factor
                    )
                    .map(q => q.response)
                    .filter(val => {
                        return val !== null && 
                               val !== undefined && 
                               val !== 'N/A' && 
                               val !== '' &&
                               !isNaN(Number(val));
                    })
                    .map(val => Number(val));
                
                // Only include this person if they answered at least 50% of questions in this factor
                const totalQuestions = factor.questions.length;
                if (personResponses.length >= Math.ceil(totalQuestions * 0.5)) {
                    const average = personResponses.reduce((sum, val) => sum + val, 0) / personResponses.length;
                    personAverages.push(average);
                }
            });
        }
        
        if (personAverages.length === 0) {
            return null;
        }
        
        // Check minimum response threshold (now based on number of people, not individual responses)
        const minimumResponses = core_data?.Whitelabel?.rule || 2;
        if (personAverages.length <= minimumResponses) return null;
        
        // Count person averages in each category (updated to handle fractional values)
        const lowCount = personAverages.filter(score => score >= customRanges.Low.min && score <= customRanges.Low.max).length;
        const neutralCount = personAverages.filter(score => score >= customRanges.Neutral.min && score <= customRanges.Neutral.max).length;
        const highCount = personAverages.filter(score => score >= customRanges.High.min && score <= customRanges.High.max).length;
        
        const totalCategorizedResponses = lowCount + neutralCount + highCount;
        if (totalCategorizedResponses === 0) return null;
        
        const distributionData = [
            { 
                name: "Low", 
                data: [(lowCount / totalCategorizedResponses) * 100], 
                color: chartOptions[0].color, 
                count: lowCount 
            },
            { 
                name: "Neutral", 
                data: [(neutralCount / totalCategorizedResponses) * 100], 
                color: chartOptions[1].color, 
                count: neutralCount 
            },
            { 
                name: "High", 
                data: [(highCount / totalCategorizedResponses) * 100], 
                color: chartOptions[2].color, 
                count: highCount 
            }
        ];
        
        return distributionData;
    };

    if (!core_data) {
        return <div>Loading...</div>;
    }


    // Get all questions from all factors
    const allQuestions = core_data.questions.dimensions.reduce((acc, dimension, i) => {
        const questions = dimension.factors.reduce((factorAcc, factor, fIndex) => {
            // Update the object structure to look like this:
            // {
            //     "q": 0, // question index within the factor.questions array
            //     "dimension": i, // index of the dimension in core_data.questions.dimensions
            //     "factor": fIndex, // index of the factor in the dimension.factors array
            //     "title": "Question 1"
            // }
            return [...factorAcc, ...factor.questions.map((q, qIndex) => ({
                q: qIndex,
                dimension: i,
                factor: fIndex,
                title: q.q,
                isOutcome: false
            }))];
        }, []);
        return [...acc, ...questions];
    }, []);

    // Add outcome questions if they exist
    if (core_data.outcomeQuestions && core_data.outcomeQuestions.questions) {
        const outcomeQuestions = core_data.outcomeQuestions.questions.reduce((acc, category, categoryIndex) => {
            const questions = category.questions.map((q) => ({
                q: q.q,
                s: q.s,
                outcomeCategory: categoryIndex,
                categoryName: category.name,
                title: q.question,
                isOutcome: true
            }));
            return [...acc, ...questions];
        }, []);
        allQuestions.push(...outcomeQuestions);
    }

    // Create factor data structure for factors view
    const allFactors = core_data.questions.dimensions.reduce((acc, dimension, dimensionIndex) => {
        const factors = dimension.factors.map((factor, factorIndex) => ({
            dimension: dimensionIndex,
            factor: factorIndex,
            dimensionName: dimension.title || dimension.name,
            title: factor.title,
            isOutcome: false,
            questionCount: factor.questions.length
        }));
        return [...acc, ...factors];
    }, []);

    // Add outcome categories as factors
    if (core_data.outcomeQuestions && core_data.outcomeQuestions.questions) {
        const outcomeFactors = core_data.outcomeQuestions.questions.map((category, categoryIndex) => ({
            outcomeCategory: categoryIndex,
            categoryName: category.name,
            title: category.name,
            isOutcome: true,
            questionCount: category.questions.length
        }));
        allFactors.push(...outcomeFactors);
    }

    console.log(core_data)

    const ScoreLegend = () => (
        <LegendContainer>
            <LegendTitle>
                <Text typographyStyle={typography.caption.semibold} color={colors.text.dark}>
                    Score Range:
                </Text>
            </LegendTitle>
            <LegendItems>
                {chartOptions.map((option) => (
                    <LegendItem key={option.name}>
                        <LegendColorBox color={option.color} />
                        {editingNeutral && option.name === 'Neutral' ? (
                            <RangeEditContainer>
                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                    {option.name} (
                                </Text>
                                <RangeInput
                                    key="neutral-min"
                                    type="number"
                                    step="0.01"
                                    value={tempNeutralValues.min}
                                    onChange={handleNeutralMinChange}
                                    onKeyDown={handleNeutralKeyDown}
                                    placeholder="min"
                                    autoFocus
                                />
                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                    -
                                </Text>
                                <RangeInput
                                    key="neutral-max"
                                    type="number"
                                    step="0.01"
                                    value={tempNeutralValues.max}
                                    onChange={handleNeutralMaxChange}
                                    onKeyDown={handleNeutralKeyDown}
                                    placeholder="max"
                                />
                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                    )
                                </Text>
                                <RangeButton onClick={handleNeutralSave}>✓</RangeButton>
                                <RangeButton onClick={handleNeutralCancel}>✕</RangeButton>
                            </RangeEditContainer>
                        ) : (
                            <ClickableRangeText 
                                onClick={option.name === 'Neutral' ? handleNeutralEdit : undefined}
                                isClickable={option.name === 'Neutral'}
                            >
                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                    {option.name} ({Math.round(customRanges[option.name].min * 100) / 100}-{Math.round(customRanges[option.name].max * 100) / 100})
                                    {option.name === 'Neutral' && (
                                        <span style={{ fontSize: '10px', color: '#999', marginLeft: '4px' }}>
                                            (click to edit)
                                        </span>
                                    )}
                                </Text>
                            </ClickableRangeText>
                        )}
                    </LegendItem>
                ))}
            </LegendItems>
        </LegendContainer>
    );

    return (
        <Container>
            <BlueTitle>Culture Analytics</BlueTitle>
            <Title>Question Analysis</Title>

            <OptionsContainer>
                {selected === null && (
                    <>
                        <ViewToggleContainer>
                            <ToggleButton 
                                isActive={viewMode === 'questions'} 
                                onClick={() => setViewMode('questions')}
                            >
                                <Text typographyStyle={typography.caption.semibold} color={viewMode === 'questions' ? '#FFFFFF' : colors.text.dark}>
                                    Questions
                                </Text>
                            </ToggleButton>
                            <ToggleButton 
                                isActive={viewMode === 'factors'} 
                                onClick={() => setViewMode('factors')}
                            >
                                <Text typographyStyle={typography.caption.semibold} color={viewMode === 'factors' ? '#FFFFFF' : colors.text.dark}>
                                    Factors
                                </Text>
                            </ToggleButton>
                        </ViewToggleContainer>
                        <SortContainer>
                            <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                Sort by:
                            </Text>
                            <SortButton onClick={toggleSortDirection}>
                                <Text typographyStyle={typography.caption.semibold} color={colors.text.dark}>
                                    {sortDirection === 'desc' ? 'High to Low' : 'Low to High'} ↕
                                </Text>
                            </SortButton>
                        </SortContainer>
                        <ScoreLegend />
                    </>
                )}
            </OptionsContainer>

            <FiltersRow>
                <Filters />
            </FiltersRow>


            <Content>
                {selected === null ? (
                    <OverviewContainer style={{ 
                        opacity: isFading || isSortTransitioning ? 0.3 : 1, 
                        transition: 'opacity 0.3s ease-in-out',
                        pointerEvents: isSortTransitioning ? 'none' : 'auto'
                    }}>
                        {(() => {
                            if (viewMode === 'questions') {
                                const questionsWithData = [...allQuestions].map(question => {
                                    const dist = computeDistributionData(question);
                                    if (!dist) return null;
                                    const questionKey = question.isOutcome 
                                        ? `outcome-${question.q}-${question.s}` 
                                        : `${question.dimension}-${question.factor}-${question.q}`;
                                    return {
                                        question,
                                        dist,
                                        questionKey
                                    };
                                }).filter(Boolean);

                                if (questionsWithData.length === 0) {
                                    const minimumResponses = core_data?.Whitelabel?.rule || 2;
                                    return (
                                        <EmptyStateContainer>
                                            <Empty 
                                                title="Insufficient Response Data"
                                                message={`Questions require more than ${minimumResponses} responses to display analytics. Please check back when more survey data is available.`}
                                            />
                                        </EmptyStateContainer>
                                    );
                                }

                                return questionsWithData.sort((a, b) => {
                                    const aHigh = a.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                    const bHigh = b.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                    return sortDirection === 'desc' ? bHigh - aHigh : aHigh - bHigh;
                                }).map(({ question, dist, questionKey }) => (
                                    <OverviewItem key={questionKey} onClick={() => handleSelect(question)}>
                                        <QuestionLabel><Text typographyStyle={typography.caption.regular} color={colors.text.dark}>{question.title}</Text></QuestionLabel>
                                        <ChartWrapper><QuestionDistributionChart data={dist} /></ChartWrapper>
                                    </OverviewItem>
                                ));
                            } else {
                                // Factors view
                                const factorsWithData = [...allFactors].map(factor => {
                                    const dist = computeFactorDistributionData(factor);
                                    if (!dist) return null;
                                    const factorKey = factor.isOutcome 
                                        ? `outcome-factor-${factor.outcomeCategory}` 
                                        : `factor-${factor.dimension}-${factor.factor}`;
                                    return {
                                        factor,
                                        dist,
                                        factorKey
                                    };
                                }).filter(Boolean);

                                if (factorsWithData.length === 0) {
                                    const minimumResponses = core_data?.Whitelabel?.rule || 2;
                                    return (
                                        <EmptyStateContainer>
                                            <Empty 
                                                title="Insufficient Response Data"
                                                message={`Factors require more than ${minimumResponses} responses to display analytics. Please check back when more survey data is available.`}
                                            />
                                        </EmptyStateContainer>
                                    );
                                }

                                return factorsWithData.sort((a, b) => {
                                    const aHigh = a.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                    const bHigh = b.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                    return sortDirection === 'desc' ? bHigh - aHigh : aHigh - bHigh;
                                }).map(({ factor, dist, factorKey }) => (
                                    <OverviewItem key={factorKey} onClick={() => handleSelect(factor)}>
                                        <QuestionLabel>
                                            <div>
                                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                                    {factor.title}
                                                </Text>
                                                <div style={{ marginTop: '2px' }}>
                                                    <Text typographyStyle={typography.caption.light} color={colors.text.light} style={{ fontSize: '11px' }}>
                                                        {factor.questionCount} question{factor.questionCount !== 1 ? 's' : ''}
                                                    </Text>
                                                </div>
                                            </div>
                                        </QuestionLabel>
                                        <ChartWrapper><QuestionDistributionChart data={dist} /></ChartWrapper>
                                    </OverviewItem>
                                ));
                            }
                        })()}
                    </OverviewContainer>
                ) : (
                    <DetailContainer style={{ 
                        opacity: isFading ? 0 : 1, 
                        transition: 'opacity 0.3s ease-in-out' 
                    }}>
                        <DetailHeader>
                            <BackButton onClick={handleBack}>Back to Overview</BackButton>
                            <Anchor anchorLabel=" " />
                        </DetailHeader>

                        <QuestionContentWrapper style={{
                            opacity: isAnchorTransitioning ? 0.3 : 1,
                            transition: 'opacity 0.3s ease-in-out'
                        }}>
                            <QuestionContent
                                question={selected}
                                core_data={core_data}
                                outcomeQ={outcomeQ}
                                data={correctedData}
                                selectedQuestion={selected}
                                outcomeStandards={outcomeStandards}
                                navigation={navigation}
                                rule={rule}
                                viewMode={viewMode}
                                isFactor={selected && (selected.questionCount !== undefined)}
                                customRanges={customRanges}
                            />
                        </QuestionContentWrapper>
                    </DetailContainer>
                )}
            </Content>
        </Container>
    );
};

export default QuestionAnalytics;

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    font-family: 'Raleway';
`;

const BlueTitle = styled.div`
    color: #2D70E2;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 15px;
    margin-top: -20px;
`;

const Title = styled.div`
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 30px;
    color: #2A3039;
    display: flex;
`;

const Content = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    margin-top: 20px;
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    min-height: 40px;
    gap: 24px;
`;

const FiltersRow = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-top: 10px;
    margin-bottom: 10px;
`;

const SortContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`;

const SortButton = styled.button`
    background: none;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #f0f0f0;
    }
`;

const LegendContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background-color: #f9fafb;
    border-radius: 6px;
    border: 1px solid #e1e5e9;
`;

const LegendTitle = styled.div`
    flex-shrink: 0;
`;

const LegendItems = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;

const LegendColorBox = styled.div`
    width: 10px;
    height: 10px;
    background-color: ${props => props.color};
    border-radius: 2px;
    flex-shrink: 0;
`;

const ChartWrapper = styled.div`
    flex: 1;
    min-width: 300px;
`;

const QuestionLabel = styled.div`
    flex: 0 0 300px;
    white-space: normal;
    word-break: break-word;
`;

const OverviewContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const OverviewItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    padding: 0px 16px;
    cursor: pointer;
    &:hover { background-color: #f5f5f5; }
`;

const DetailContainer = styled.div`
    width: 100%;
    padding: 20px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    color: #2D70E2;
    cursor: pointer;
    padding: 0;
    font-size: 12px;
    font-family: 'Raleway', sans-serif;
`;

const DetailHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
`;

const QuestionContentWrapper = styled.div`
    width: 100%;
    height: 100%;
`;

const EmptyStateContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 400px;
    padding: 40px;
`;

const ViewToggleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    background-color: #f1f3f4;
    border-radius: 6px;
    padding: 2px;
`;

const ToggleButton = styled.button`
    background: ${props => props.isActive ? '#6B7280' : 'transparent'};
    border: none;
    padding: 6px 12px;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: ${props => props.isActive ? '#6B7280' : '#e8eaed'};
    }
`;

const ClickableRangeText = styled.div`
    cursor: ${props => props.isClickable ? 'pointer' : 'default'};
    padding: 2px 4px;
    border-radius: 3px;
    transition: background-color 0.2s ease;
    
    &:hover {
        background-color: ${props => props.isClickable ? '#f0f0f0' : 'transparent'};
    }
`;

const RangeEditContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 4px;
`;

const RangeInput = styled.input`
    width: 50px;
    padding: 2px 4px;
    border: 1px solid #ccc;
    border-radius: 3px;
    font-size: 11px;
    text-align: center;
    background-color: white;
    
    &:focus {
        outline: none;
        border-color: #2D70E2;
        box-shadow: 0 0 0 1px rgba(45, 112, 226, 0.1);
    }
    
    &::placeholder {
        color: #999;
        opacity: 0.7;
    }
`;

const RangeButton = styled.button`
    background: none;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    font-size: 12px;
    color: #666;
    border-radius: 3px;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #f0f0f0;
        color: #333;
    }
    
    &:first-of-type {
        color: #28a745;
        
        &:hover {
            color: #1e7e34;
        }
    }
    
    &:last-of-type {
        color: #dc3545;
        
        &:hover {
            color: #c82333;
        }
    }
`;